import { useLocation, useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { TradeTermsLayout } from "../../components/tradeTermsAssignment/TradeTermsLayout";
import { DeliveryMethodDetails } from "./DeliveryMethodDetails";
import { Accordion } from "@sellar-io/components";
import { useFulfillmentType } from "../../hooks/api/v2/fulfillmentType/useFulfillmentType";
import { FulfillmentTypeGetResponseType } from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "../../components";
import { DeliveryCharges } from "./components/DeliveryCharges";
import { FreeDeliveryOffers } from "./components/FreeDeliveryOffers";
import { DeliveryMethodOrderRules } from "./components/DeliveryMethodOrderRules";
import { useFulfillmentTypeAssign } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeAssign";
import { useFulfillmentTypeGetStats } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeGetStats";
import { capitalize } from "@sellar-io/common";
import { useMemo } from "react";

export const DeliveryMethod = () => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const deliveryMethodId = useParamAsNumber(
    ROUTES.DELIVERY_METHOD,
    "deliveryMethodId",
  );

  const redirectToCustomerZoneId = state?.customerZoneRedirectId ?? null;
  const redirectToDeliveryZoneId = state?.deliveryZoneRedirectId ?? null;
  const launchAssignmentDialog = state?.launchAssignmentDialog ?? false;

  const { data: deliveryMethod, isLoading } = useFulfillmentType(
    deliveryMethodId,
    {
      enabled: !!deliveryMethodId,
    },
  );

  const { data: deliveryMethodStats } = useFulfillmentTypeGetStats(
    deliveryMethodId,
    {
      enabled: !!deliveryMethodId,
    },
  );

  const deliveryMethodAssign = useFulfillmentTypeAssign(deliveryMethodId);

  const closeLink = useMemo(() => {
    if (redirectToCustomerZoneId) {
      return `${ROUTES.CUSTOMER_ZONE.href({ customerZoneId: redirectToCustomerZoneId })}?tab=deliveryMethod`;
    }
    if (redirectToDeliveryZoneId) {
      return `${ROUTES.DELIVERY_ZONE.href({ deliveryZoneId: redirectToDeliveryZoneId })}?tab=deliveryMethod`;
    }
    return new URLSearchParams(search).get("view") === "container-rules"
      ? `${ROUTES.DELIVERY_METHODS}?view=container-rules`
      : ROUTES.DELIVERY_METHODS;
  }, [redirectToCustomerZoneId, redirectToDeliveryZoneId, search]);

  return (
    <TradeTermsLayout
      title={
        deliveryMethod?.name ??
        capitalize(deliveryMethod?.fulfillmentType ?? "")
      }
      onClickLeftIcon={() => navigate(closeLink)}
      aside={<DeliveryMethodDetails />}
      type="delivery method"
      fulfillmentType={deliveryMethod?.fulfillmentType}
      isNew={false}
      existingConnections={deliveryMethodStats?.SupplierRetailerConnections}
      existingCustomerZoneIds={deliveryMethodStats?.customerZonesAssigned.map(
        (cz) => cz.id,
      )}
      onAssign={({
        connectionIds,
        customerZoneIds,
      }: {
        connectionIds: number[];
        customerZoneIds: number[];
      }) => {
        return new Promise((resolve) => {
          deliveryMethodAssign.mutate(
            {
              connectionIds,
              customerZoneIds,
            },
            {
              onSuccess: () => {
                resolve({
                  success: true,
                  message: "",
                });
              },
              onError: () => {
                resolve({
                  success: false,
                  message: "Failed to assign Delivery method, please try again",
                });
              },
            },
          );
        });
      }}
      showAssignment={launchAssignmentDialog}
      assignmentHeader={
        <>
          <span className="font-semibold">Assign delivery method</span>
          <p className="text-zinc-500">
            You can choose to assign your delivery method to your specified
            customer zones or individual customers. Any edits you make to this
            delivery method will affect all customers you've assigned it to.
          </p>
        </>
      }
    >
      <div className="mx-auto flex w-full max-w-[920px] items-center gap-2">
        {isLoading ? (
          <SpinnerLegacy />
        ) : (
          <DeliveryMethodContent deliveryMethod={deliveryMethod} />
        )}
      </div>
    </TradeTermsLayout>
  );
};

export const DeliveryMethodContent = ({
  deliveryMethod,
  readOnly,
}: {
  deliveryMethod?: Omit<
    FulfillmentTypeGetResponseType,
    "deliveryMethodContainerRules"
  >;
  readOnly?: boolean;
}) => {
  const hasOrderRules =
    deliveryMethod?.OrderRules &&
    (deliveryMethod.OrderRules.PriceTierOrderRules.length > 0 ||
      deliveryMethod.OrderRules.minimumOrderPrice !== null);

  const hasDeliveryCharges =
    deliveryMethod &&
    (deliveryMethod.FulfillmentTypeContainerDeliveryCharges.length > 0 ||
      deliveryMethod.charge !== null);

  const hasDeliveryOffers =
    deliveryMethod &&
    (deliveryMethod.ContainerTypeDeliveryChargeRules.length > 0 ||
      deliveryMethod.freeDeliveryBasketSubtotal !== null);

  const initialOpenIds = useMemo(
    () =>
      [
        hasOrderRules ? "order-rules" : null,
        hasDeliveryCharges ? "delivery-charges" : null,
        hasDeliveryOffers ? "delivery-offers" : null,
      ].filter((id) => id !== null),
    [hasOrderRules, hasDeliveryCharges, hasDeliveryOffers],
  );

  return (
    <div className="mb-48 flex w-full flex-col gap-4 px-4">
      {deliveryMethod && (
        <Accordion
          initialOpenIds={initialOpenIds}
          items={[
            {
              id: "order-rules",
              title: "Order rules",
              closedIcon: hasOrderRules ? "CHEVRON_DOWN" : "PLUS",
              children: (
                <DeliveryMethodOrderRules
                  deliveryMethod={deliveryMethod}
                  readOnly={readOnly}
                />
              ),
            },
            {
              id: "delivery-charges",
              title: "Delivery charges",
              closedIcon: hasDeliveryCharges ? "CHEVRON_DOWN" : "PLUS",
              children: (
                <DeliveryCharges
                  deliveryMethod={deliveryMethod}
                  readOnly={readOnly}
                />
              ),
            },
            {
              id: "delivery-offers",
              title: "Free delivery offers",
              closedIcon: hasDeliveryOffers ? "CHEVRON_DOWN" : "PLUS",
              children: (
                <FreeDeliveryOffers
                  deliveryMethod={deliveryMethod}
                  readOnly={readOnly}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
};
